import React from "react";
import { useHandlerContext } from "./providers/Handler";
import { useAppContext } from "../../AppProvider";
import AsyncSelect from "../../Components/Selects/AsyncSelect/AsyncSelect";
import InputIcon from "../../Components/Inputs/InputIcon/InputIcon";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getFieldError } from "../../utilities/utilities";
import { getAllStoreIntegrations } from "../../Components/Selects/AsyncSelect/api";
import { FetchParam } from "../../Models/FetchParam";
import {
  faICursor,
  faLoader,
  faArrowLeft,
  faEuro,
  faPercentage,
  faRulerHorizontal,
  faRulerVertical,
  faRuler,
  faWeightHanging,
  faCube,
} from "../../utilities/fontawesomeIcons";
import { Article } from "../../Models/Article";
import { Link } from "react-router-dom";
import { StoreIntegration } from "../../Models/StoreIntegration";
import { Checkbox } from "../../Components/Checkboxs/Checkbox/Checkbox";
import { ImageProvider } from "./providers/Image";
import { Image } from "./Image";

export const Handler = () => {
  const { dictionary } = useAppContext();
  const { data, action, error, loader, edit } = useHandlerContext();

  const titles: Record<string, string> = {
    create: dictionary.words.new_sm,
    clone: dictionary.actions.duplicate,
    edit: dictionary.actions.edit,
    details: dictionary.actions.details,
  };

  const handleChange = (
    name: string,
    value: string | boolean | number | object | null,
    key: string
  ) => {
    if (action !== "details") {
      edit(
        {
          ...data,
          [name]: value,
        } as Article,
        key as keyof Article
      );
    }
  };

  return (
    <React.Fragment>
      <div className="row align-items-center mb-2">
        <div className="col">
          <h2 className="m-0">
            {titles[action]} {dictionary.products.product}
          </h2>
        </div>
      </div>

      <div
        className="d-grid grid-column mt-1"
        style={{
          gridTemplateColumns: `1fr 2fr`,
        }}
      >
        <div className="box-container h-100">
          <ImageProvider action={action}>
            <Image />
          </ImageProvider>
        </div>
        <div className="box-container h-100">
          {loader ? (
            <div className="text-center mt-2">
              <FontAwesomeIcon icon={faLoader} spin size="2x" />
            </div>
          ) : (
            <React.Fragment>
              <h3>{dictionary.words.general_informations}</h3>
              <div
                className="d-grid grid-column mb-2"
                style={{
                  gridTemplateColumns: `1fr 2fr`,
                }}
              >
                <div>
                  <label className="label-2">
                    {dictionary.integrations.store_integration}
                  </label>
                  <AsyncSelect
                    name="sourceIntegration"
                    optionValue="id"
                    optionLabel="name"
                    value={data.sourceIntegration ?? null}
                    onSearch={(
                      name: string,
                      value: StoreIntegration | StoreIntegration[] | null
                    ) => {
                      handleChange(name, value, "sourceIntegration");
                    }}
                    loadOptions={(inputValue, pagination) => {
                      const params: FetchParam = {
                        inputValue,
                        pagination,
                        sorting: { selector: "name", orderBy: "asc" },
                      };
                      return getAllStoreIntegrations(params);
                    }}
                    isClearable={false}
                    isDisabled={action === "details"}
                    error={getFieldError(error, "sourceIntegration")}
                    placeholder={
                      action !== "details" ? dictionary.actions.select : ""
                    }
                  />
                </div>
                <div>
                  <label className="label-2">
                    {dictionary.registries.name}
                  </label>
                  <InputIcon
                    name="name"
                    value={data.name || ""}
                    type="text"
                    placeholder="Penna a sfera"
                    onChange={({ target }) => {
                      handleChange(target.name, target.value, "name");
                    }}
                    icon={faICursor}
                    disabled={action === "details"}
                    error={getFieldError(error, "name")}
                  />
                </div>
              </div>
              <div
                className="d-grid grid-column mb-2"
                style={{
                  gridTemplateColumns: `1fr 1fr 1fr 1fr`,
                }}
              >
                <div>
                  <label className="label-2">
                    {dictionary.references.external_reference}
                  </label>
                  <InputIcon
                    name="externalReference"
                    value={data.externalReference || ""}
                    type="text"
                    placeholder="EXTREF123"
                    onChange={({ target }) => {
                      handleChange(
                        target.name,
                        target.value,
                        "externalReference"
                      );
                    }}
                    disabled={action === "details"}
                    icon={faICursor}
                    error={getFieldError(error, "externalReference")}
                  />
                </div>
                <div>
                  <label className="label-2">
                    {dictionary.registries.vendor}
                  </label>
                  <InputIcon
                    name="vendor"
                    value={data.vendor || ""}
                    type="text"
                    onChange={({ target }) => {
                      handleChange(target.name, target.value, "vendor");
                    }}
                    disabled={action === "details"}
                    icon={faICursor}
                    error={getFieldError(error, "vendor")}
                  />
                </div>
                <div>
                  <label className="label-2">{dictionary.words.typology}</label>
                  <InputIcon
                    name="type"
                    value={data.type || ""}
                    type="text"
                    onChange={({ target }) => {
                      handleChange(target.name, target.value, "type");
                    }}
                    disabled={action === "details"}
                    icon={faICursor}
                    error={getFieldError(error, "type")}
                  />
                </div>
                <div>
                  <label className="label-2">
                    {dictionary.products.barcode}
                  </label>
                  <InputIcon
                    name="barcode"
                    value={data.barcode || ""}
                    type="text"
                    onChange={({ target }) => {
                      handleChange(target.name, target.value, "barcode");
                    }}
                    disabled={action === "details"}
                    icon={faICursor}
                    error={getFieldError(error, "barcode")}
                  />
                </div>
              </div>
              <div
                className="d-grid grid-column mb-2"
                style={{
                  gridTemplateColumns: `1fr 1fr 1fr`,
                }}
              >
                <div>
                  <label className="label-2">{dictionary.words.price}</label>
                  <InputIcon
                    name="price"
                    value={data.price}
                    type="positive-number"
                    onChange={({ target }) => {
                      handleChange(target.name, target.value, "price");
                    }}
                    disabled={action === "details"}
                    icon={faEuro}
                    error={getFieldError(error, "price")}
                  />
                </div>
                <div>
                  <label className="label-2">
                    {dictionary.words.original_price}
                  </label>
                  <InputIcon
                    name="originalPrice"
                    value={data.originalPrice}
                    type="positive-number"
                    onChange={({ target }) => {
                      handleChange(target.name, target.value, "originalPrice");
                    }}
                    disabled={action === "details"}
                    icon={faEuro}
                    error={getFieldError(error, "originalPrice")}
                  />
                </div>
                <div>
                  <label className="label-2">{dictionary.words.vat}</label>
                  <InputIcon
                    name="vat"
                    value={data.vat}
                    type="positive-integer-number"
                    onChange={({ target }) => {
                      handleChange(target.name, target.value, "vat");
                    }}
                    disabled={action === "details"}
                    icon={faPercentage}
                    error={getFieldError(error, "vat")}
                  />
                </div>
              </div>
              <div
                className="d-grid grid-column mb-2"
                style={{
                  gridTemplateColumns: `1fr 1fr 1fr`,
                }}
              >
                <div>
                  <Checkbox
                    name="pub"
                    checked={data.pub || false}
                    onChange={({ target }) => {
                      handleChange(target.name, !data.pub, "pub");
                    }}
                    disabled={action === "details"}
                    label={dictionary.messages.visible_to_sub_organization}
                  />
                </div>
                <div>
                  <Checkbox
                    name="shippingRequired"
                    checked={data.shippingRequired || false}
                    onChange={() => {
                      if (action !== "details") {
                        const newData = { ...data };
                        newData["shippingRequired"] = !data.shippingRequired;
                        if (!newData.shippingRequired) {
                          delete newData.width;
                          delete newData.height;
                          delete newData.length;
                          delete newData.weight;
                        }
                        edit(
                          newData as Article,
                          "shippingRequired" as keyof Article
                        );
                      }
                    }}
                    disabled={action === "details"}
                    label={dictionary.products.physical_good}
                  />
                </div>
                <div>
                  <Checkbox
                    name="uniqueBarcodeItem"
                    checked={data.uniqueBarcodeItem || false}
                    onChange={({ target }) => {
                      handleChange(
                        target.name,
                        !data.uniqueBarcodeItem,
                        "uniqueBarcodeItem"
                      );
                    }}
                    disabled={action === "details"}
                    label={dictionary.products.unique_barcode}
                  />
                </div>
              </div>
              {data.shippingRequired && (
                <>
                  <h3>{dictionary.dimensions.dimensions}</h3>
                  <div
                    className="d-grid grid-column"
                    style={{
                      gridTemplateColumns: `1fr 1fr 1fr 1fr 1fr`,
                    }}
                  >
                    <div>
                      <label className="label-2">
                        {dictionary.dimensions.width} (cm)
                      </label>
                      <InputIcon
                        name="width"
                        value={data.width}
                        type="positive-integer-number"
                        placeholder="10"
                        onChange={({ target }) => {
                          handleChange(target.name, target.value, "width");
                        }}
                        disabled={action === "details"}
                        icon={faRulerHorizontal}
                        error={getFieldError(error, "width")}
                      />
                    </div>
                    <div>
                      <label className="label-2">
                        {dictionary.dimensions.height} (cm)
                      </label>
                      <InputIcon
                        name="height"
                        value={data.height}
                        type="positive-integer-number"
                        placeholder="10"
                        onChange={({ target }) => {
                          handleChange(target.name, target.value, "height");
                        }}
                        disabled={action === "details"}
                        icon={faRulerVertical}
                        error={getFieldError(error, "height")}
                      />
                    </div>
                    <div>
                      <label className="label-2">
                        {dictionary.dimensions.length} (cm)
                      </label>
                      <InputIcon
                        name="length"
                        value={data.length}
                        type="positive-integer-number"
                        placeholder="10"
                        onChange={({ target }) => {
                          handleChange(target.name, target.value, "length");
                        }}
                        disabled={action === "details"}
                        icon={faRuler}
                        error={getFieldError(error, "length")}
                      />
                    </div>
                    <div>
                      <label className="label-2">
                        {dictionary.dimensions.weight} (kg)
                      </label>
                      <InputIcon
                        name="weight"
                        value={data.weight}
                        type="positive-number"
                        placeholder="1.00"
                        onChange={({ target }) => {
                          handleChange(target.name, target.value, "weight");
                        }}
                        disabled={action === "details"}
                        icon={faWeightHanging}
                        error={getFieldError(error, "weight")}
                      />
                    </div>
                    <div>
                      <label className="label-2">
                        {dictionary.dimensions.volume} (m3)
                      </label>
                      <InputIcon
                        name="volume"
                        value={
                          data.width && data.height && data.length
                            ? (data.width * data.height * data.length) / 1000000
                            : undefined
                        }
                        type="positive-number"
                        precision={6}
                        disabled={true}
                        icon={faCube}
                      />
                    </div>
                  </div>
                </>
              )}
            </React.Fragment>
          )}
        </div>
      </div>

      <div className="row mt-2">
        <div className="col">
          {
            <Link to={`/articles`} className="button-1 button-1-transparent">
              <FontAwesomeIcon icon={faArrowLeft} /> {dictionary.words.back}
            </Link>
          }
        </div>
      </div>
    </React.Fragment>
  );
};
